import {SingleTokenPriceInput} from '#/atoms/single-token-price-input';
import {VerticalSeparator} from '#/atoms/vertical-separator';
import {ActionMenuContext} from '#/components/actionmenu/action-menu';
import {useCoinDisplayPreferences} from '#/hooks/use-coin-display-preferences';
import {decimalsMultiplier} from '#/utils/contract-consts';
import {stringToTokenAmount, tokenAmountToInputString} from '#/utils/token-string-utils';
import {ArrowDownIcon, ArrowUpIcon} from '@heroicons/react/24/solid';
import * as RadioGroup from '@radix-ui/react-radio-group';
import * as Tabs from '@radix-ui/react-tabs';
import {FunctionComponent, ReactNode, useContext, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {TransactionType} from 'shareland-common';

enum MarketLimitTabKeys {
  MARKET = 'market',
  LIMIT = 'limit',
}

export const ActionMenuTradeMarketLimitTabs: FunctionComponent<{children: {market: ReactNode; limit: ReactNode}}> = ({
  children,
}) => {
  const {t} = useTranslation('translation');

  const [selectedTab, setSelectedTab] = useState<MarketLimitTabKeys>(MarketLimitTabKeys.MARKET);
  const onValueChange = (value: string) => {
    setSelectedTab(value as MarketLimitTabKeys);
  };

  return (
    <Tabs.Root
      className='relative h-full flex flex-col items-stretch'
      value={selectedTab}
      onValueChange={onValueChange}
    >
      <Tabs.List className='self-end w-1/2 flex bg-graybluedark relative rounded-full mb-3'>
        <Tabs.Trigger
          className='z-10 w-1/2 p-2 border-0 rounded-full uppercase text-xs text-white font-semibold cursor-pointer outline-none'
          value={MarketLimitTabKeys.MARKET}
        >
          {t('pages.actionMenu.trade.marketLimit.market')}
        </Tabs.Trigger>

        <Tabs.Trigger
          className='z-10 w-1/2 p-2 border-0 rounded-full uppercase text-xs text-white font-semibold cursor-pointer outline-none'
          value='limit'
        >
          {t('pages.actionMenu.trade.marketLimit.limit')}
        </Tabs.Trigger>

        <div
          style={{
            width: `${100 / Object.values(MarketLimitTabKeys).length}%`,
            transform: `translateX(${Object.values(MarketLimitTabKeys).indexOf(selectedTab) * 100}%)`,
          }}
          className='absolute top-0 h-full p-1 z-0 transition-transform'
        >
          <div className='w-full h-full bg-deepblue rounded-full' />
        </div>
      </Tabs.List>

      <Tabs.Content className='data-[state=active]:grow flex flex-col' value={MarketLimitTabKeys.MARKET}>
        {children.market}
      </Tabs.Content>

      <Tabs.Content className='data-[state=active]:grow flex flex-col' value={MarketLimitTabKeys.LIMIT}>
        {children.limit}
      </Tabs.Content>
    </Tabs.Root>
  );
};

type ValueInputOnChange = {
  value: number;
  input: string;
  onValueChange: (value: number, setInput?: boolean) => void;
  onInputChange: (value: string) => void;
};

export const usePriceAmountSubtotal = (
  initialPrice: number,
): {
  price: ValueInputOnChange;
  amount: ValueInputOnChange;
  subtotal: ValueInputOnChange;
} => {
  const displayPref = useCoinDisplayPreferences();

  // Price
  const [priceValue, priceSetValue] = useState(initialPrice);
  const [priceInput, priceSetInput] = useState(
    tokenAmountToInputString(displayPref.usdcToNumericDisplayAmount(priceValue), 2),
  );
  const priceOnChange = (newPrice: number, setInput = false) => {
    if (setInput) {
      priceSetInput(tokenAmountToInputString(displayPref.usdcToNumericDisplayAmount(newPrice), 2));
    }
    priceSetValue(newPrice);

    subtotalUpdateFromPriceAndAmount(newPrice, amountValue);
  };
  const priceOnInputChange = (amount: string) => {
    priceSetInput(amount);
    const newPrice = stringToTokenAmount(amount);
    const newPriceAdjustedForUSD = displayPref.numericDisplayAmountToUsdc(newPrice);

    priceOnChange(newPriceAdjustedForUSD);
  };

  // Amount
  const [amountValue, amountSetValue] = useState(0);
  const [amountInput, amountSetInput] = useState('');
  const amountOnChange = (newAmount: number, setInput = false) => {
    if (setInput) {
      amountSetInput(tokenAmountToInputString(newAmount, 6));
    }
    amountSetValue(newAmount);

    subtotalUpdateFromPriceAndAmount(priceValue, newAmount);
  };
  const amountOnInputChange = (newAmountInput: string) => {
    amountSetInput(newAmountInput);
    const newAmount = stringToTokenAmount(newAmountInput);

    amountOnChange(newAmount);
  };

  // Subtotal
  const [subtotalValue, subtotalSetValue] = useState(0);
  const [subtotalInput, subtotalSetInput] = useState('');
  const subtotalOnChange = (newSubtotal: number, setInput = false) => {
    if (setInput) {
      subtotalSetInput(tokenAmountToInputString(displayPref.usdcToNumericDisplayAmount(newSubtotal), 2));
    }
    subtotalSetValue(newSubtotal);

    const newAmount = Math.ceil((newSubtotal / priceValue) * decimalsMultiplier);
    amountSetInput(tokenAmountToInputString(newAmount, 6));
    amountSetValue(newAmount);
  };
  const subtotalOnInputChange = (newSubtotalInput: string) => {
    subtotalSetInput(newSubtotalInput);

    const newSubtotal = stringToTokenAmount(newSubtotalInput);
    const newSubtotalAdjustedForUSD = displayPref.numericDisplayAmountToUsdc(newSubtotal);

    subtotalOnChange(newSubtotalAdjustedForUSD);
  };
  const subtotalUpdateFromPriceAndAmount = (newPrice: number, newAmount: number) => {
    const newSubtotal = (newPrice * newAmount) / decimalsMultiplier;

    subtotalSetValue(newSubtotal);
    subtotalSetInput(tokenAmountToInputString(displayPref.usdcToNumericDisplayAmount(newSubtotal), 2));
  };

  return {
    price: {
      value: priceValue,
      input: priceInput,
      onValueChange: priceOnChange,
      onInputChange: priceOnInputChange,
    },
    amount: {
      value: amountValue,
      input: amountInput,
      onValueChange: amountOnChange,
      onInputChange: amountOnInputChange,
    },
    subtotal: {
      value: subtotalValue,
      input: subtotalInput,
      onValueChange: subtotalOnChange,
      onInputChange: subtotalOnInputChange,
    },
  };
};

const LimitInputPriceAmountPercentButton: FunctionComponent<{
  type: TransactionType.PurchaseLimitOrder | TransactionType.SellLimitOrder;
  price: ValueInputOnChange;
  percentage: number;
}> = ({type, price, percentage}) => {
  const {landMarketInfo} = useContext(ActionMenuContext);

  const {t} = useTranslation('translation');

  const ArrowIcon = type === TransactionType.PurchaseLimitOrder ? ArrowDownIcon : ArrowUpIcon;
  const directionMultiplier = type === TransactionType.PurchaseLimitOrder ? -1 : 1;

  const getPriceByPercentage = (percentage: number) =>
    (landMarketInfo.marketPrice * (100 + percentage * directionMultiplier)) / 100;

  const value = getPriceByPercentage(percentage);

  const onClick = () => {
    price.onValueChange(value, true);
  };

  return (
    <RadioGroup.Item
      className='relative bg-graybluedark2 data-[state=checked]:bg-deepblue text-xs text-white rounded flex-center hover:opacity-70 disabled:grayscale disabled:opacity-50 disabled:pointer-events-none px-1'
      onClick={onClick}
      value={`${percentage}`}
      checked={price.value === value}
    >
      {t('utils.percentageFixed', {value: percentage, decimals: 0})}
      <ArrowIcon className='w-2 aspect-square fill-white stroke-white' />
    </RadioGroup.Item>
  );
};

export const LimitOrderTokenInputs: FunctionComponent<
  ReturnType<typeof usePriceAmountSubtotal> & {
    type: TransactionType.PurchaseLimitOrder | TransactionType.SellLimitOrder;
  }
> = ({type, price, amount, subtotal}) => {
  const {land} = useContext(ActionMenuContext);
  const displayPref = useCoinDisplayPreferences();

  return (
    <div className='space-y-3'>
      <div className='flex items-stretch gap-2'>
        <SingleTokenPriceInput
          className='grow'
          title={'Price'}
          tokenName={displayPref.symbol}
          tokenAmount={price.input}
          onChange={price.onInputChange}
        />

        <RadioGroup.Root className='w-24 py-1 grid grid-cols-2 grid-rows-2 gap-1' defaultValue='0'>
          <LimitInputPriceAmountPercentButton type={type} percentage={0} price={price} />
          <LimitInputPriceAmountPercentButton type={type} percentage={1} price={price} />
          <LimitInputPriceAmountPercentButton type={type} percentage={5} price={price} />
          <LimitInputPriceAmountPercentButton type={type} percentage={10} price={price} />
        </RadioGroup.Root>
      </div>

      <VerticalSeparator />

      <SingleTokenPriceInput
        className='grow'
        title={'Amount'}
        tokenName={land.tokenSymbol}
        tokenAmount={amount.input}
        onChange={amount.onInputChange}
      />

      <VerticalSeparator />

      <SingleTokenPriceInput
        className='mb-4'
        title={'Subtotal'}
        tokenName={displayPref.symbol}
        tokenAmount={subtotal.input}
        onChange={subtotal.onInputChange}
      />
    </div>
  );
};
